import React, { useState } from "react"; // Importer useState
import {
  ShoppingCart,
  MessageCircle,
  User,
  Menu as MenuIcon,
  X, // Importer l'icône X pour fermer
  UserPlus, // Optionnel si vous voulez la garder
  LogOut, // Icône de déconnexion
  ListOrdered, // Icône pour l'historique des commandes
} from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import managerLogo from "../assets/manager_mini_logo.png";
import { getItem, removeItem } from "../utils/localStorage";

interface HeaderProps {
  currentPage: string;
  setCurrentPage: (
    page:
      | "menu"
      | "cart"
      | "checkout"
      | "tracking"
      | "service"
      | "profile"
      | "order-history"
  ) => void;
  cartItemsCount: number;
  user: any;
  onOpenAuth: (mode: "login" | "register") => void;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({
  currentPage,
  setCurrentPage,
  cartItemsCount,
  user,
  onOpenAuth,
  onLogout,
}) => {
  const { t } = useTranslation();
  // NOUVEAU: État pour gérer l'ouverture du menu mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Fonction utilitaire pour naviguer et fermer le menu
  const handleNavigation = (page: HeaderProps["currentPage"]) => {
    setCurrentPage(page);
    setIsMobileMenuOpen(false); // Ferme le menu après la navigation
  };

  const handleLogout = () => {
    onLogout();
    setIsMobileMenuOpen(false);
  };

  const handleOpenAuth = (mode: "login" | "register") => {
    onOpenAuth(mode);
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div
            className="flex items-center cursor-pointer"
            onClick={() => handleNavigation("menu")}
          >
            <img src={managerLogo} alt={t("header.appName")} className="h-10" />
          </div>

          {/* Navigation - Desktop */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => setCurrentPage("menu")}
              className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                currentPage === "menu"
                  ? "text-orange-500 font-bold"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              {t("header.menu")}
            </button>
            <button
              onClick={() => setCurrentPage("service")}
              className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                currentPage === "service"
                  ? "text-orange-500 font-bold"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              {t("header.customerService")}
            </button>
          </nav>

          {/* Actions & Buttons */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Language Selector (Visible sur tous les écrans) */}
            <LanguageSelector />

            {/* Cart (Visible sur tous les écrans) */}
            <button
              onClick={() => handleNavigation("cart")}
              data-cart-button
              className="relative p-2 text-gray-600 hover:text-black transition-colors duration-200"
            >
              <ShoppingCart className="h-5 w-5" strokeWidth={1.5} />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {cartItemsCount}
                </span>
              )}
            </button>

            {/* Auth Buttons - Desktop */}
            {/* Reste inchangé pour le desktop */}
            {!user ? (
              <div className="hidden md:flex items-center space-x-3">
                <button
                  onClick={() => onOpenAuth("login")}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-black transition-colors duration-200"
                >
                  <User className="w-4 h-4" strokeWidth={1.5} />
                  <span>{t("header.login")}</span>
                </button>
                <button
                  onClick={() => onOpenAuth("register")}
                  className="btn-primary text-sm"
                >
                  <span>{t("header.register")}</span>
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center space-x-4">
                <button
                  onClick={() => setCurrentPage("profile")}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-black transition-colors duration-200"
                >
                  <User className="w-4 h-4" strokeWidth={1.5} />
                  <span>{t("header.profile")}</span>
                </button>
                <button
                  onClick={() => setCurrentPage("order-history")}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-black transition-colors duration-200"
                >
                  <span>{t("header.myOrders", "My Orders")}</span>
                </button>
                <span className="text-sm text-gray-600">
                  {t("header.hello")},{" "}
                  <span className="font-medium text-black">
                    {user.firstName}
                  </span>
                </span>
                <button
                  onClick={onLogout}
                  className="text-sm text-gray-500 hover:text-orange-500 transition-colors duration-200"
                >
                  {t("header.logout")}
                </button>
              </div>
            )}

            {/* Mobile Menu Button (Hamburger) */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-600 hover:text-black transition-colors duration-200 md:hidden"
              aria-label={
                isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"
              }
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" strokeWidth={1.5} />
              ) : (
                <MenuIcon className="h-6 w-6" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Content (S'affiche lors de l'ouverture) */}
      <div
        className={`md:hidden absolute w-full bg-white shadow-lg border-t border-gray-100 transition-all duration-300 ease-in-out ${
          isMobileMenuOpen
            ? "max-h-screen opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="flex flex-col py-2">
          {/* Navigation Links */}
          <button
            onClick={() => handleNavigation("menu")}
            className={`flex items-center w-full px-5 py-3 text-left text-base font-medium transition-colors duration-200 ${
              currentPage === "menu"
                ? "text-orange-500 bg-orange-50"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            {t("header.menu")}
          </button>
          <button
            onClick={() => handleNavigation("service")}
            className={`flex items-center w-full px-5 py-3 text-left text-base font-medium transition-colors duration-200 ${
              currentPage === "service"
                ? "text-orange-500 bg-orange-50"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <MessageCircle className="w-5 h-5 mr-3" strokeWidth={1.5} />
            {t("header.customerService")}
          </button>

          {/* User Section - Logged In */}
          {user && (
            <>
              <div className="px-5 py-3 text-sm text-gray-500 border-t border-gray-100 mt-2">
                {t("header.hello")},{" "}
                <span className="font-medium text-black">{user.firstName}</span>
              </div>
              <button
                onClick={() => handleNavigation("profile")}
                className="flex items-center w-full px-5 py-3 text-left text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <User className="w-5 h-5 mr-3" strokeWidth={1.5} />
                {t("header.profile")}
              </button>
              <button
                onClick={() => handleNavigation("order-history")}
                className="flex items-center w-full px-5 py-3 text-left text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <ListOrdered className="w-5 h-5 mr-3" strokeWidth={1.5} />
                {t("header.myOrders", "My Orders")}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-5 py-3 text-left text-base font-medium text-red-600 hover:bg-red-50 transition-colors duration-200"
              >
                <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
                {t("header.logout")}
              </button>
            </>
          )}

          {/* User Section - Logged Out */}
          {!user && (
            <div className="flex flex-col border-t border-gray-100 pt-2">
              <button
                onClick={() => handleOpenAuth("login")}
                className="flex items-center w-full px-5 py-3 text-left text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <User className="w-5 h-5 mr-3" strokeWidth={1.5} />
                {t("header.login")}
              </button>
              <button
                onClick={() => handleOpenAuth("register")}
                className="flex items-center w-full px-5 py-3 text-left text-base font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 transition-colors duration-200"
              >
                <UserPlus className="w-5 h-5 mr-3" strokeWidth={1.5} />
                {t("header.register")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
